// ScheduleLibrary.js
import React, { useState } from 'react';
import WebsiteNavbar from './lib/html/WebsiteNavbar';
import WebsiteFooter from './lib/html/WebsiteFooter';

import schedulelibrary from './lib/images/schedule-library.png';

import daily_schedule from '../../images/daily_schedule.png';
import block_schedule from '../../images/block_schedule.png';
import ab_schedule from '../../images/ab_schedule.png';
import ab_skinny_schedule from '../../images/ab_skinny_schedule.png';

const schedules = [
  {
    name: 'Traditional - Daily',
    summary: 'Daily class periods meeting every day.',
	img: daily_schedule,
    pros: [
		{
			heading:'Continuity and retention',
			description:'Teachers see students daily, which helps with information retention and review.'
		},{
			heading:'Easier absences',
			description:'Students who miss a class can catch up more easily than with block scheduling.'
		},{
			heading:'Student engagement',
			description:'Shorter periods can lead to increased student focus and engagement.'
		},{
			heading:'Simplified scheduling',
			description:'Easier for students and administration to schedule courses.'
		},{
			heading:'Improved teacher-student relationships',
			description:'Some studies show that when teachers see their students daily and consistently, it helps build stronger relationships.'
		}
    ],
    cons: [
      	{
			heading:'Pacing',
			description:'Teachers might rush through material to cover the curriculum in shorter instructional periods.'
		},{
			heading:'Less in-depth learning',
			description:'Students get less time to deeply explore subjects.'
		},{
			heading:'Logistical challenges',
			description:'This approach may not work as well for labs or hands-on activities that require more time.'
		},{
			heading:'Frequent transitions',
			description:'Moving between many classes each day wastes instructional time and can be stressful for students.'
		},{
			heading:'Higher workload in a day',
			description:'Students juggle homework, assignments, and tests for multiple subjects at once, increasing stress.'
		},{
			heading:'Not aligned with teen sleep needs',
			description:'Standard schedules often start early in the morning, conflicting with adolescent sleep patterns and potentially hurting academic performance.'
		}
    ]
  },
  {
    name: 'Rotating Drop',
    summary: 'Classes rotate through periods, meeting at different times every day and getting dropped once during the cycle.',
	img: block_schedule,
    pros: [
      	{
			heading:'Increased instructional time',
			description:'By eliminating two class periods each day, the remaining class periods can be longer. This allows for deeper dives into subject matter and more hands-on activities, labs, or group projects.'
		},{
			heading:'More courses per year',
			description:'Schools can offer students more classes, often eight per year instead of seven, allowing for a greater variety of electives, Advanced Placement (AP) courses, and college-credit options.'
		},{
			heading:'Greater student engagement',
			description:'Longer class periods and the variety of a rotating schedule can help prevent fatigue and keep students more engaged than a rigid, repetitive daily schedule.'
		},{
			heading:'Mitigate the impact of absences',
			description:"When a student is absent or frequently late, they don't miss the same class over and over. This is particularly beneficial for student-athletes, who may have regular afternoon dismissals for games."
		},{
			heading:'Fairer distribution of class times',
			description:"Since the rotation ensures classes meet at different times of day, every class gets a chance to meet during students' and teachers' most productive hours."
		},{
			heading:'Common "flex" period',
			description:'This schedule can incorporate a common period where all students and teachers are available at the same time. This can be used for extra help, study halls, advisory meetings, or club activities.'
		},{
			heading:'Improved school climate',
			description:'By reducing the number of daily class changes, these schedules can lessen the chaotic feel of crowded hallways and reduce disciplinary referrals.'
		}
    ],
    cons: [
     	 {
			heading:'Complicated to track',
			description:'The rotational cycle can be confusing for students, parents, and even staff to follow, particularly at the beginning. It requires a significant adjustment from a static, daily routine.'
		},{
			heading:'Inconsistent momentum',
			description:'Teachers may feel they see their students less frequently than in a traditional schedule, making it difficult to maintain academic momentum, especially in classes that meet only a few times a week.'
		},{
			heading:'Disruption from extended breaks',
			description:'The "drop" aspect can be problematic when factoring in school holidays or other breaks. Important instructional time can be lost, and momentum can be broken when a class goes several days without meeting.'
		},{
			heading:'Difficult to schedule events',
			description:"The complex rotation makes it challenging to schedule school-wide events, guest speakers, or field trips, as every day is different."
		},{
			heading:'Potential for missed learning',
			description:"If a student is absent, they may miss a longer block of instructional time than they would in a traditional, shorter-period schedule. The extended time between classes can also make it harder for some students to retain information."
		},{
			heading:'Less helpful for some learners',
			description:'While some students thrive with the added flexibility, others do better with the rigid structure of a traditional, daily schedule.'
		},{
			heading:'Challenges in shared facilities',
			description:'If a common "flex" period is implemented, it can create logistical challenges for school facilities like the cafeteria, potentially leading to overcrowding.'
		}
    ]
  },
  {
    name: 'A/B Block',
    summary: 'Classes meet every other day on an A/B rotation.',
	img: ab_schedule,
    pros: [
      	{
			heading:'In-depth learning',
			description:'Allows for deeper exploration and more focused learning experiences in fewer subjects.'
		},{
			heading:'Project-based learning',
			description:'Provides more time for engaging in complex projects and inquiry-based activities.'
		},{
			heading:'Improved student performance',
			description:'Some studies show improved grades, higher honor roll numbers, and reduced failure rates.'
		}
    ],
    cons: [
      	{
			heading:'Fatigue',
			description:'Longer class periods can lead to student fatigue, impacting concentration.'
		},{
			heading:'Loss of continuity',
			description:'Students miss significant content when absent, and there is a longer gap between seeing a teacher.'
		},{
			heading:'Absence impact',
			description:'Missing one block class can feel like a greater setback than missing a traditional, shorter period.'
		}
    ]
  },
  {
    name: 'A/B Skinny',
    summary: 'Classes meet every other day on an A/B rotation, with an additional "skinny day" where all classes meet.',
	img: ab_skinny_schedule,
    pros: [
     	{
			heading:'Blends advantages of other schedule types',
			description:'Provides strengths of both a traditional standard bell and a block schedule'
		},{
			heading:'Allows for academic support',
			description:'Provides time for students to seek academic help from teachers during the school week'
		},{
			heading:'Deeper learning',
			description:'Offers teachers an opportunity to go deeper into content and create more in-depth lesson plans'
		},{
			heading:'Better continuity than pure A/B',
			description:'Supports block schedules while giving daily exposure to certain subjects'
		},{
			heading:'Allows for advisory',
			description:'Gives an opportunity for a regular advisory period'
		}
    ],
    cons: [
      	{
			heading:'Requires tracking of day type',
			description:'Schools need to communicate to students and parents which day type it is'
		},{
			heading:'Adjusting lesson planning',
			description:'Teachers will need support to implement instructional practices that take advantage of longer periods'
		},{
			heading:'Loss of continuity',
			description:'Teachers of foreign language and math may express concern about not seeing students every day'
		}
    ]
  },
  {
    name: '4x4 Block',
    summary: 'Four longer class periods per day, semesters rotate.',
	img: daily_schedule,
    pros: [
     	 {
			heading:'Reduced homework and simplified focus',
			description:'Students concentrate on only four subjects at a time, making their homework load more manageable on a daily basis. This can help reduce stress and improve focus.'
		},{
			heading:'Deeper learning opportunities',
			description:'Longer class periods allow for more varied and engaging instructional strategies, including hands-on labs, collaborative group work, and in-depth discussions. This can lead to a deeper understanding of the material.'
		},{
			heading:'Greater course flexibility',
			description:'With eight course slots per year (instead of six or seven in a traditional schedule), students have more opportunities to take elective classes or repeat a failed course without falling behind. This can make it easier to meet graduation requirements and explore interests.'
		},{
			heading:'Accelerated learning',
			description:"High-achieving students can double up on certain subjects, like math, to move through the curriculum at an accelerated pace."
		},{
			heading:'Smoother college transition',
			description:"A 4x4 schedule more closely resembles the semester system used by many colleges, which can help prepare students for the demands of higher education."
		},{
			heading:'Improved behavior',
			description:'Some schools report decreases in discipline problems and dropout rates after implementing a 4x4 block schedule.'
		},{
			heading:'Fewer students and preparations per day',
			description:'Teachers see fewer students and have fewer courses to plan for each day, allowing them to build stronger relationships with students and their families.'
		},{
			heading:'Flexibility and time for varied instruction',
			description:'Longer periods give teachers the flexibility to use a variety of teaching methods, rather than being confined to shorter, lecture-based sessions.'
		},{
			heading:'More planning time',
			description:'In a 4x4 system, teachers typically have a daily extended planning period, since they are teaching only three courses.'
		}
    ],
    cons: [
     	 {
			heading:'"Use it or lose it" knowledge gaps',
			description:'The rotational cycle can be confusing for students, parents, and even staff to follow, particularly at the beginning. It requires a significant adjustment from a static, daily routine.'
		},{
			heading:'Potential for information overload',
			description:'Teachers may feel they see their students less frequently than in a traditional schedule, making it difficult to maintain academic momentum, especially in classes that meet only a few times a week.'
		},{
			heading:'Greater impact of absences',
			description:'The "drop" aspect can be problematic when factoring in school holidays or other breaks. Important instructional time can be lost, and momentum can be broken when a class goes several days without meeting.'
		},{
			heading:'Challenges for AP courses',
			description:"The complex rotation makes it challenging to schedule school-wide events, guest speakers, or field trips, as every day is different."
		},{
			heading:'Intensive content delivery',
			description:"If a student is absent, they may miss a longer block of instructional time than they would in a traditional, shorter-period schedule. The extended time between classes can also make it harder for some students to retain information."
		},{
			heading:'Adjusting lesson planning',
			description:'While some students thrive with the added flexibility, others do better with the rigid structure of a traditional, daily schedule.'
		},{
			heading:'Maintaining student engagement',
			description:'If a common "flex" period is implemented, it can create logistical challenges for school facilities like the cafeteria, potentially leading to overcrowding.'
		},{
			heading:'Difficult to schedule',
			description:'For counselors and school leaders, building a block schedule is more complex and tedious than a traditional one, especially when incorporating career and technical education (CTE) courses, AP classes, and other specialized programs.'
		},{
			heading:'Inconclusive academic research',
			description:'Studies on the effectiveness of block scheduling are mixed, with results showing inconsistent links to improved student test scores. Some research suggests it might not improve achievement on standardized tests compared to traditional schedules.'
		}
    ]
  }
];

export default function ScheduleLibrary() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div id="homepage-content">
      <WebsiteNavbar />

      {/* HERO */}
      <section className="library-hero">
        <div className="library-hero-inner">
			<h1 className='white-text'>Schedule Library</h1>
			<p>
				Explore common high school schedule types — and understand the tradeoffs
				of moving from one bell schedule to another.
			</p>
			<img src={schedulelibrary} alt='Scheduling Library'/>
        </div>
      </section>

      {/* SCHEDULE LIST */}
      <section className="library-list">
        <div className="library-list-inner grid-mobilize">
          {schedules.map((schedule, index) => (
            <div key={schedule.name} className={`schedule-card ${openIndex === index ? 'open' : ''}`} onClick={() => setOpenIndex(openIndex === index ? null : index)}>
				<div className='schedule-card-top'>
					<img className='schedule-library-image' src={schedule.img} alt={`${schedule.name} schedule preview`} />
					<button className="schedule-header">
						<div>
							<h3>{schedule.name}</h3>
							<p>{schedule.summary}</p>
						</div>
						<span className="expand-indicator">{openIndex === index ? '−' : '+'}</span>
					</button>
				</div>

              {openIndex === index && (
                <div className="schedule-details">
                  <div className="pros-cons">
                    <div>
                      <h4>Pros</h4>
                      <div>
                        {schedule.pros.map((item, i) => {
							const pro_heading = item.heading;
							const pro_description = item.description;

							return (
								 <div className='schedule-library-pro-con' key={`pro-${index}-${i}`}>
									<div className='schedule-library-pro-number'>{i+1}</div>
									<strong className='schedule-library-pro-heading'>{pro_heading}</strong>
									<div className='schedule-library-pro-con-description'>{pro_description}</div>
								</div>
							)
                         
						})}
                      </div>
                    </div>
                    <div>
                      <h4>Cons</h4>
                      <div>
                        {schedule.cons.map((item, i) => {
                          	const con_heading = item.heading;
							const con_description = item.description;

							return (
								 <div className='schedule-library-pro-con' key={`con-${index}-${i}`}>
									<div className='schedule-library-con-number'>{i+1}</div>
									<strong className='schedule-library-con-heading'>{con_heading}</strong>
									<div className='schedule-library-pro-con-description'>{con_description}</div>
								</div>
							)
						})}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="library-cta">
        <h2>Not Sure Which Schedule Fits Your School?</h2>
        <p>We help schools model and compare schedules before making a decision.</p>
        <a id='library-cta-btn' href="/contact">Talk to an Expert</a>
      </section>

      <WebsiteFooter />
    </div>
  );
}
